import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export interface FilterCriteria {
  column: string;
  operator: string;
  value: any;
}

export interface SavedFilterConfig {
  name: string;
  filters: FilterCriteria[];
  timestamp: number;
}

export interface FilterOperator {
  value: string;
  label: string;
}

@Injectable({
  providedIn: "root",
})
export class FilterService {
  // Map to store active filters for different components
  private activeFiltersMap: Map<string, BehaviorSubject<FilterCriteria[]>> =
    new Map();

  // Map to store saved filters for different components
  private savedFiltersMap: Map<string, BehaviorSubject<SavedFilterConfig[]>> =
    new Map();

  public availableOperators: FilterOperator[] = [
    { value: "contains", label: "Contains" },
    { value: "equals", label: "Equals" },
    { value: "startsWith", label: "Starts with" },
    { value: "endsWith", label: "Ends with" },
    { value: "greaterThan", label: "Greater than" },
    { value: "lessThan", label: "Less than" },
  ];

  constructor() {}

  // Active filters methods
  getActiveFilters(storageKey: string): Observable<FilterCriteria[]> {
    if (!this.activeFiltersMap.has(storageKey)) {
      this.activeFiltersMap.set(
        storageKey,
        new BehaviorSubject<FilterCriteria[]>([])
      );
    }
    return this.activeFiltersMap.get(storageKey)!.asObservable();
  }

  getCurrentActiveFilters(storageKey: string): FilterCriteria[] {
    if (!this.activeFiltersMap.has(storageKey)) {
      this.activeFiltersMap.set(
        storageKey,
        new BehaviorSubject<FilterCriteria[]>([])
      );
    }
    return this.activeFiltersMap.get(storageKey)!.getValue();
  }

  setActiveFilters(filters: FilterCriteria[], storageKey: string): void {
    if (!this.activeFiltersMap.has(storageKey)) {
      this.activeFiltersMap.set(
        storageKey,
        new BehaviorSubject<FilterCriteria[]>([])
      );
    }
    this.activeFiltersMap.get(storageKey)!.next(filters);
    this.saveActiveFiltersToStorage(filters, storageKey);
  }

  addFilter(filter: FilterCriteria, storageKey: string): void {
    const currentFilters = this.getCurrentActiveFilters(storageKey);
    const newFilters = [...currentFilters, filter];
    this.setActiveFilters(newFilters, storageKey);
  }

  removeFilter(index: number, storageKey: string): void {
    const currentFilters = this.getCurrentActiveFilters(storageKey);
    const newFilters = currentFilters.filter((_, i) => i !== index);
    this.setActiveFilters(newFilters, storageKey);
  }

  clearAllFilters(storageKey: string): void {
    this.setActiveFilters([], storageKey);
  }

  // Saved filters methods
  getSavedFilters(
    storageKey: string = "default"
  ): Observable<SavedFilterConfig[]> {
    if (!this.savedFiltersMap.has(storageKey)) {
      this.savedFiltersMap.set(
        storageKey,
        new BehaviorSubject<SavedFilterConfig[]>([])
      );
    }
    return this.savedFiltersMap.get(storageKey)!.asObservable();
  }

  getCurrentSavedFilters(storageKey: string = "default"): SavedFilterConfig[] {
    if (!this.savedFiltersMap.has(storageKey)) {
      this.savedFiltersMap.set(
        storageKey,
        new BehaviorSubject<SavedFilterConfig[]>([])
      );
    }
    return this.savedFiltersMap.get(storageKey)!.getValue();
  }

  setSavedFilters(filters: SavedFilterConfig[], storageKey: string): void {
    if (!this.savedFiltersMap.has(storageKey)) {
      this.savedFiltersMap.set(
        storageKey,
        new BehaviorSubject<SavedFilterConfig[]>([])
      );
    }
    this.savedFiltersMap.get(storageKey)!.next(filters);
    this.saveSavedFiltersToStorage(filters, storageKey);
  }

  saveFilterConfig(
    name: string,
    filters: FilterCriteria[],
    storageKey: string
  ): void {
    const currentConfigs = this.getCurrentSavedFilters(storageKey);
    const existingIndex = currentConfigs.findIndex(
      (config) => config.name === name
    );

    if (existingIndex !== -1) {
      currentConfigs.splice(existingIndex, 1);
    }

    const newConfig: SavedFilterConfig = {
      name,
      filters: [...filters],
      timestamp: Date.now(),
    };

    const newConfigs = [...currentConfigs, newConfig];
    this.setSavedFilters(newConfigs, storageKey);
  }

  deleteFilterConfig(index: number, storageKey: string): void {
    const currentConfigs = this.getCurrentSavedFilters(storageKey);
    const newConfigs = currentConfigs.filter((_, i) => i !== index);
    this.setSavedFilters(newConfigs, storageKey);
  }

  loadFilterConfig(config: SavedFilterConfig, storageKey: string): void {
    this.setActiveFilters([...config.filters], storageKey);
  }

  // Storage methods
  loadActiveFiltersFromStorage(storageKey: string): void {
    try {
      const storedFilters = localStorage.getItem(storageKey);
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);
        if (!this.activeFiltersMap.has(storageKey)) {
          this.activeFiltersMap.set(
            storageKey,
            new BehaviorSubject<FilterCriteria[]>([])
          );
        }
        this.activeFiltersMap.get(storageKey)!.next(parsedFilters);
      }
    } catch (error) {
      console.error(
        `Error loading active filters from localStorage for ${storageKey}:`,
        error
      );
      if (!this.activeFiltersMap.has(storageKey)) {
        this.activeFiltersMap.set(
          storageKey,
          new BehaviorSubject<FilterCriteria[]>([])
        );
      }
    }
  }

  saveActiveFiltersToStorage(
    filters: FilterCriteria[],
    storageKey: string
  ): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (error) {
      console.error(
        `Error saving active filters to localStorage for ${storageKey}:`,
        error
      );
    }
  }

  loadSavedFiltersFromStorage(storageKey: string): void {
    try {
      const storedSavedFilters = localStorage.getItem(storageKey);
      if (storedSavedFilters) {
        const parsedFilters = JSON.parse(storedSavedFilters);
        if (!this.savedFiltersMap.has(storageKey)) {
          this.savedFiltersMap.set(
            storageKey,
            new BehaviorSubject<SavedFilterConfig[]>([])
          );
        }
        this.savedFiltersMap.get(storageKey)!.next(parsedFilters);
      }
    } catch (error) {
      console.error(
        `Error loading saved filters from localStorage for ${storageKey}:`,
        error
      );
      if (!this.savedFiltersMap.has(storageKey)) {
        this.savedFiltersMap.set(
          storageKey,
          new BehaviorSubject<SavedFilterConfig[]>([])
        );
      }
    }
  }

  saveSavedFiltersToStorage(
    filters: SavedFilterConfig[],
    storageKey: string
  ): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (error) {
      console.error(
        `Error saving filter configurations to localStorage for ${storageKey}:`,
        error
      );
    }
  }

  // Add a method to delay filter application until data is loaded
  public delayedFilter(callback: () => void, delay: number = 300): void {
    setTimeout(callback, delay);
  }

  // Data filtering method
  createFilterPredicate(
    columnMap: Map<string, string>,
    valueGetter: (data: any, column: string) => any
  ) {
    return (data: any, filterString: string) => {
      // Extract the components of the filter string
      const [searchTerm, category, filtersJson] = filterString.split("||");
      let activeFilters: FilterCriteria[] = [];

      try {
        if (filtersJson) {
          activeFilters = JSON.parse(filtersJson);
        }
      } catch (error) {
        console.error("Error parsing filters JSON:", error);
      }

      // Apply basic search if provided
      let searchMatch = true;
      if (searchTerm) {
        const searchTermLower = searchTerm.toLowerCase();
        searchMatch = Object.keys(data).some((key) => {
          const value = data[key];
          return value && String(value).toLowerCase().includes(searchTermLower);
        });
      }

      // Apply category filter if provided
      let categoryMatch = true;
      // This would need to be implemented specifically for each component
      // since category filtering is domain-specific

      // Apply advanced filters
      let advancedFiltersMatch = true;
      if (activeFilters.length > 0) {
        advancedFiltersMatch = activeFilters.every((filter) => {
          const value = valueGetter(data, filter.column);

          switch (filter.operator) {
            case "contains":
              return String(value)
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
            case "equals":
              return (
                String(value).toLowerCase() ===
                String(filter.value).toLowerCase()
              );
            case "startsWith":
              return String(value)
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase());
            case "endsWith":
              return String(value)
                .toLowerCase()
                .endsWith(String(filter.value).toLowerCase());
            case "greaterThan":
              return Number(value) > Number(filter.value);
            case "lessThan":
              return Number(value) < Number(filter.value);
            default:
              return true;
          }
        });
      }

      return searchMatch && categoryMatch && advancedFiltersMatch;
    };
  }
}
